import React, { useRef, useState } from "react";
import { Upload, Loader2, FileSpreadsheet, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { importCatalogPreview, importCatalogCommit } from "../lib/api";
import { fmtPrice } from "./Catalog";

// AI import: PDF / Excel / CSV / TXT → review → save
export default function CatalogImportDialog({ open, onClose, companyId, onDone }) {
  const [stage, setStage] = useState("pick"); // pick | analyzing | review | saving
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});
  const fileRef = useRef(null);

  const reset = () => { setStage("pick"); setItems([]); setChecked({}); };
  const close = () => { reset(); onClose(); };

  const analyze = async (file) => {
    if (!file) return;
    setStage("analyzing");
    try {
      const r = await importCatalogPreview(companyId, file);
      if (!r.items.length) { toast.error("Nenhum produto ou serviço encontrado nesse arquivo."); setStage("pick"); return; }
      setItems(r.items); setChecked(Object.fromEntries(r.items.map((_, i) => [i, true]))); setStage("review");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível analisar o arquivo"); setStage("pick");
    }
  };
  const edit = (i, k, v) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const selected = items.filter((_, i) => checked[i]);

  const commit = async () => {
    if (!selected.length) return;
    setStage("saving");
    try {
      const payload = selected.map((it) => ({ ...it, price: it.price === "" || it.price == null ? null : Number(String(it.price).replace(",", ".")) }));
      const r = await importCatalogCommit(companyId, payload);
      toast.success(`${r.created} ${r.created === 1 ? "item importado" : "itens importados"}${r.skipped ? ` · ${r.skipped} não couberam no limite do plano` : ""}`);
      onDone(); close();
    } catch { toast.error("Não foi possível salvar os itens"); setStage("review"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl rounded-3xl p-0 overflow-hidden" data-testid="catalog-import-dialog">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-[17px]">Importar catálogo com IA</DialogTitle>
          <DialogDescription className="text-[12.5px]">Envie uma tabela de preços, cardápio ou lista (PDF com texto, Excel .xlsx, CSV, TXT ou Word). A IA identifica os itens e você revisa antes de salvar. Preços nunca são inventados.</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6">
          {stage === "pick" && (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="catalog-import-drop"
              className="mt-2 w-full rounded-2xl ring-1 ring-dashed ring-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors p-10 grid place-items-center text-center">
              <Upload size={22} className="text-slate-400" />
              <span className="mt-3 text-[13.5px] font-medium text-slate-800">Escolher arquivo</span>
              <span className="mt-1 text-[12px] text-slate-500">PDF · XLSX · CSV · TXT · DOCX — até 8 MB</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xlsm,.csv,.txt,.md,.tsv,.docx" className="hidden" data-testid="catalog-import-input" onChange={(e) => { analyze(e.target.files?.[0]); e.target.value = ""; }} />
          {stage === "analyzing" && <div className="py-14 grid place-items-center text-center"><Loader2 size={22} className="animate-spin text-slate-400" /><p className="mt-3 text-[13px] text-slate-600">A IA está lendo o arquivo e separando os itens…</p></div>}
          {(stage === "review" || stage === "saving") && (
            <>
              <div className="mt-1 flex items-center justify-between text-[12.5px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><FileSpreadsheet size={14} /> {items.length} itens encontrados · {selected.length} selecionados</span>
                <button type="button" onClick={() => setChecked(Object.fromEntries(items.map((_, i) => [i, selected.length !== items.length])))} className="underline" data-testid="catalog-import-toggle-all">{selected.length === items.length ? "Desmarcar todos" : "Marcar todos"}</button>
              </div>
              <div className="mt-3 max-h-[48vh] overflow-y-auto rounded-2xl ring-1 ring-slate-200 divide-y divide-slate-100" data-testid="catalog-import-list">
                {items.map((it, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 ${checked[i] ? "" : "opacity-50"}`} data-testid={`catalog-import-row-${i}`}>
                    <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))} className="mt-2 h-4 w-4 accent-slate-900" data-testid={`catalog-import-check-${i}`} />
                    <div className="flex-1 grid sm:grid-cols-[1fr_120px] gap-2">
                      <div className="min-w-0">
                        <input value={it.name} onChange={(e) => edit(i, "name", e.target.value)} className="w-full h-8 rounded-lg ring-1 ring-slate-200 px-2 text-[13px] font-medium" data-testid={`catalog-import-name-${i}`} />
                        <input value={it.description || ""} onChange={(e) => edit(i, "description", e.target.value)} placeholder="Descrição (opcional)" className="mt-1 w-full h-7 rounded-lg ring-1 ring-slate-100 px-2 text-[12px] text-slate-600" />
                        <div className="mt-1 flex gap-1.5 text-[11px] text-slate-500">
                          <select value={it.kind} onChange={(e) => edit(i, "kind", e.target.value)} className="h-6 rounded-md ring-1 ring-slate-200 px-1 bg-white"><option value="service">Serviço</option><option value="product">Produto</option></select>
                          <input value={it.category || ""} onChange={(e) => edit(i, "category", e.target.value)} placeholder="Categoria" className="h-6 w-32 rounded-md ring-1 ring-slate-200 px-1.5" />
                        </div>
                      </div>
                      <div>
                        <input value={it.price == null ? "" : it.price} onChange={(e) => edit(i, "price", e.target.value)} placeholder="Sob consulta" inputMode="decimal" className="w-full h-8 rounded-lg ring-1 ring-slate-200 px-2 text-[13px] text-right" data-testid={`catalog-import-price-${i}`} />
                        <div className="mt-1 text-[11px] text-right text-slate-400">{fmtPrice(it.price === "" ? null : it.price, it.unit)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={reset} className="rounded-full h-9 text-[13px]">Outro arquivo</Button>
                <Button type="button" onClick={commit} disabled={!selected.length || stage === "saving"} className="rounded-full h-9 bg-slate-900 hover:bg-slate-800 text-[13px] gap-1.5" data-testid="catalog-import-commit">
                  {stage === "saving" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Importar {selected.length} {selected.length === 1 ? "item" : "itens"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
